import {action, computed, makeObservable, observable, override} from 'mobx';

import backend from '@lexian/api/documents-api';
import {searchUsers} from '_/store/searchUsers';
import {DateRangeFilter, Filter} from '_/utilities';
import {PaginatedItemLoader} from '_/utilities/simple-fetch';
import {
  CommonRequestOptions,
  DocumentType,
  PaginatedEndpointArguments,
} from '_/utilities/types';

export type FetchDocumentsFilterArguments = Omit<
  Parameters<typeof backend.getDocuments>[0]['parameters'],
  | keyof CommonRequestOptions
  | keyof PaginatedEndpointArguments
  | 'sortFields'
  | 'sortOrders'
  | 'casusId'
  | 'folderId'
>;

export type FetchDeletedDocumentsFilterArguments = Omit<
  Parameters<typeof backend.getDeletedDocuments>[0]['parameters'],
  | keyof CommonRequestOptions
  | keyof PaginatedEndpointArguments
  | 'sortFields'
  | 'sortOrders'
  | 'casusId'
  | 'folderId'
>;

const defaultMobxAnnotations = {
  tagFilter: observable,
  documentTypeFilter: observable,
  createdByFilter: observable,
  createdAtFilter: observable,
  query: observable,
  userList: observable,
  fetchFilterValues: computed,
  hasActiveFilter: computed,
  reset: action.bound,
};
export class DocumentFilters {
  public tagFilter = new Filter<string[]>([]);
  public documentTypeFilter = new Filter<DocumentType[]>([]);
  public createdByFilter = new Filter<string[]>([]);
  public createdAtFilter = new DateRangeFilter([undefined, undefined]);

  public query = new Filter<string>('');

  public userList = new PaginatedItemLoader(searchUsers);

  public constructor() {
    makeObservable(this, defaultMobxAnnotations);
  }

  public get fetchFilterValues(): FetchDocumentsFilterArguments {
    const [fromInclusive, toInclusive] = this.createdAtFilter.item;

    return {
      tagIds: this.tagFilter.item,
      documentTypes: this.documentTypeFilter.item,
      createdTimestampFromInclusive: fromInclusive,
      createdTimestampToInclusive: toInclusive,
      ownerIds: this.createdByFilter.item,
      query: this.query.nulledValue,
    };
  }

  public get hasActiveFilter(): boolean {
    return (
      !this.tagFilter.isEmpty ||
      !this.documentTypeFilter.isEmpty ||
      !this.createdByFilter.isEmpty ||
      !this.createdAtFilter.isEmpty ||
      !this.query.isEmpty
    );
  }

  public reset(): void {
    this.tagFilter = new Filter<string[]>([]);
    this.documentTypeFilter = new Filter<DocumentType[]>([]);
    this.createdByFilter = new Filter<string[]>([]);
    this.createdAtFilter = new DateRangeFilter([undefined, undefined]);
    this.query = new Filter<string>('');
  }
}

export class DeletedDocumentFilters extends DocumentFilters {
  public deletingUserFilter = new Filter<string[]>([]);

  public constructor() {
    super();
    makeObservable(this, {
      reset: override,
      hasActiveFilter: override,
      fetchFilterValues: override,
    });
  }

  public get fetchFilterValues(): FetchDeletedDocumentsFilterArguments {
    return {
      ...super.fetchFilterValues,
      deletingUserIds: this.deletingUserFilter.item,
    };
  }

  public get hasActiveFilter(): boolean {
    return super.hasActiveFilter && !this.deletingUserFilter.isEmpty;
  }

  public reset(): void {
    super.reset();
    this.deletingUserFilter = new Filter<string[]>([]);
  }
}
